import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { adminAuthApi } from '../lib/admin-api';
import type { 
  HRUser,
  HRLoginRequest
} from '../types/auth';

// Admin Auth State
interface AdminAuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  hrUser: HRUser | null;
  sessionToken: string | null;
  error: string | null;
}

// Admin Auth Context Type
interface AdminAuthContextType extends AdminAuthState {
  hrLogin: (email: string, password: string) => Promise<{
    success: boolean;
    message: string;
    error?: string;
  }>;
  logout: () => Promise<void>;
  clearError: () => void;
  refreshSession: () => Promise<void>;
}

// Auth State Actions
type AdminAuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_AUTHENTICATED'; payload: { hrUser: HRUser; sessionToken: string } }
  | { type: 'SET_UNAUTHENTICATED' }
  | { type: 'CLEAR_ERROR' };

// Initial state
const initialState: AdminAuthState = {
  isAuthenticated: false,
  isLoading: true, // Start as loading to check existing session
  hrUser: null,
  sessionToken: null,
  error: null,
};

// Auth reducer
function adminAuthReducer(state: AdminAuthState, action: AdminAuthAction): AdminAuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    case 'SET_AUTHENTICATED':
      return {
        ...state,
        isAuthenticated: true,
        hrUser: action.payload.hrUser,
        sessionToken: action.payload.sessionToken,
        error: null,
        isLoading: false,
      };
    case 'SET_UNAUTHENTICATED':
      return {
        ...state,
        isAuthenticated: false,
        hrUser: null,
        sessionToken: null,
        error: null,
        isLoading: false,
      };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

// Create context
const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

// Admin Auth Provider Component
interface AdminAuthProviderProps {
  children: React.ReactNode;
}

export function AdminAuthProvider({ children }: AdminAuthProviderProps) {
  const [state, dispatch] = useReducer(adminAuthReducer, initialState);

  // Check existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      // Skip admin session check if we're on candidate routes (but not HR login)
      const currentPath = window.location.pathname;
      const isCandidateRoute = !currentPath.startsWith('/admin') && !currentPath.startsWith('/auth/hr-login') && currentPath !== '/';
      
      if (isCandidateRoute) {
        dispatch({ type: 'SET_UNAUTHENTICATED' });
        return;
      }

      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        
        // Check for HR token
        const hrToken = localStorage.getItem('hr_token');
        if (hrToken) {
          try {
            const hrResponse = await adminAuthApi.getSession(hrToken);
            if ('success' in hrResponse && hrResponse.success) {
              dispatch({ 
                type: 'SET_AUTHENTICATED', 
                payload: { 
                  hrUser: hrResponse.user,
                  sessionToken: hrToken
                } 
              });
              return;
            } else {
              // Invalid HR token, remove it
              localStorage.removeItem('hr_token');
            }
          } catch (error) {
            // Invalid HR token, remove it
            localStorage.removeItem('hr_token');
          }
        }
        
        // No valid token found
        dispatch({ type: 'SET_UNAUTHENTICATED' });
      } catch (error) {
        dispatch({ type: 'SET_UNAUTHENTICATED' });
      }
    };

    checkSession();
  }, []);

  // HR login
  const hrLogin = useCallback(async (email: string, password: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const request: HRLoginRequest = { email, password };
      const response = await adminAuthApi.login(request);

      if ('success' in response && response.success) {
        // Store token in localStorage for HR users
        localStorage.setItem('hr_token', response.token);
        
        dispatch({ 
          type: 'SET_AUTHENTICATED', 
          payload: { 
            hrUser: response.user,
            sessionToken: response.token 
          } 
        });
        
        return { 
          success: true, 
          message: response.message 
        };
      } else {
        const error = 'error' in response ? response.error : 'HR login failed';
        dispatch({ type: 'SET_ERROR', payload: error });
        return { 
          success: false, 
          message: 'HR login failed', 
          error 
        };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Network error occurred';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      return { 
        success: false, 
        message: 'HR login failed', 
        error: errorMessage 
      };
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      const hrToken = localStorage.getItem('hr_token');
      if (hrToken) {
        // HR user logout
        await adminAuthApi.logout(hrToken);
        localStorage.removeItem('hr_token');
      }
    } catch (error) {
      // Continue with local logout even if API call fails
    }
    dispatch({ type: 'SET_UNAUTHENTICATED' });
  }, []);

  // Refresh session
  const refreshSession = useCallback(async () => {
    const hrToken = localStorage.getItem('hr_token');
    if (hrToken) {
      const hrResponse = await adminAuthApi.getSession(hrToken);
      if ('success' in hrResponse && hrResponse.success) {
        dispatch({ 
          type: 'SET_AUTHENTICATED', 
          payload: { 
            hrUser: hrResponse.user,
            sessionToken: hrToken
          } 
        });
      } else {
        localStorage.removeItem('hr_token');
        throw new Error('Session refresh failed');
      }
    } else {
      throw new Error('No token found');
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const contextValue: AdminAuthContextType = {
    ...state,
    hrLogin,
    logout,
    clearError,
    refreshSession,
  };

  return (
    <AdminAuthContext.Provider value={contextValue}>
      {children}
    </AdminAuthContext.Provider>
  );
}

// Custom hook to use admin auth context
export function useAdminAuth(): AdminAuthContextType {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
}

// Hook to check if admin is authenticated
export function useAdminAuthGuard(): { 
  isAuthenticated: boolean; 
  isLoading: boolean;
  sessionToken: string | null;
} {
  const { isAuthenticated, isLoading, sessionToken } = useAdminAuth();
  return { isAuthenticated, isLoading, sessionToken };
}

// Company permission utilities for admin context
export type CompanyAction = 'create' | 'read' | 'update' | 'delete' | 'logo';

// Permission matrix for company operations
const COMPANY_PERMISSIONS: Record<string, CompanyAction[]> = {
  HR_STAFF: ['read'],
  HR_ADMIN: ['read', 'update', 'logo'],
  GROUP_ADMIN: ['create', 'read', 'update', 'delete', 'logo'],
};

// Hook for company permissions in admin context
export function useAdminCompanyPermissions() {
  const { hrUser } = useAdminAuth();

  const hasCompanyPermission = useCallback((action: CompanyAction): boolean => {
    if (!hrUser) return false;
    const permissions = COMPANY_PERMISSIONS[hrUser.role] || [];
    return permissions.includes(action);
  }, [hrUser]);

  const canAccessCompany = useCallback((companyId: string): boolean => {
    if (!hrUser) return false;
    
    // GROUP_ADMIN has access to all companies
    if (hrUser.role === 'GROUP_ADMIN') {
      return true;
    }
    
    // Other roles need company in their scope
    return hrUser.scopeCompanyIds.includes(companyId);
  }, [hrUser]);

  const canCreateCompany = useCallback((): boolean => {
    return hasCompanyPermission('create');
  }, [hasCompanyPermission]);

  const canEditCompany = useCallback((companyId: string): boolean => {
    return hasCompanyPermission('update') && canAccessCompany(companyId);
  }, [hasCompanyPermission, canAccessCompany]);

  const canDeleteCompany = useCallback((): boolean => {
    return hasCompanyPermission('delete');
  }, [hasCompanyPermission]);

  const canUploadLogo = useCallback((companyId: string): boolean => {
    return hasCompanyPermission('logo') && canAccessCompany(companyId);
  }, [hasCompanyPermission, canAccessCompany]);

  const getAccessibleCompanyIds = useCallback((): string[] => {
    if (!hrUser) return [];
    
    // GROUP_ADMIN has access to all companies (return empty array to indicate "all")
    if (hrUser.role === 'GROUP_ADMIN') {
      return [];
    }
    
    // Other roles return their scope
    return hrUser.scopeCompanyIds;
  }, [hrUser]);

  return {
    hasCompanyPermission,
    canAccessCompany,
    canCreateCompany,
    canEditCompany,
    canDeleteCompany,
    canUploadLogo,
    getAccessibleCompanyIds,
    userRole: hrUser?.role,
    scopeCompanyIds: hrUser?.scopeCompanyIds || [],
  };
}